import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import VideoMain from "../video/VideoMain";
import MyTimer from "../../components/Timer";
import { AuthContext, UnitContext } from "../../contexts/contextExporter";
import PracticeLoader from "../practice/PracticeLoader";
import UnitFinished from "./UnitFinished";
import { partModeLabels } from "../../utils/static";
import clsx from "clsx";
import sound from "./../../assets/voice.mp3";
import FontSizeControl from "../../components/FontSizeControl";

export default function UnitCurrentContent({
  children,
}: {
  children: ReactNode;
}) {
  const { next, pin, currentPart, currentMl, unit, isLive, isUnitFinished } =
    useContext(UnitContext);
  const { user } = useContext(AuthContext);
  const notificationSound = useMemo(() => new Audio(sound), []);
  const shouldZoomVisible = isLive && pin?.partMode === "ZOOM";

  const [time, setTime] = useState<Date | null>(null);

  useEffect(() => {
    if (pin?.partMode === "PRACTICE" && currentPart?.practice_duration) {
      const extraMs = time ? time.getTime() - Date.now() : 0;
      const newTime = new Date(
        Date.now() +
        parseInt(currentPart.practice_duration) * 60 * 1000 +
        Math.max(0, extraMs)
      );
      setTime(newTime);
    } else if (pin?.partMode === "VIDEO") {
      setTime(new Date(Date.now() + 30000));
    }

    if (pin?.partMode === "VIDEO" && document.visibilityState !== "visible" && isLive) {
      let blink = true;
      const interval = setInterval(() => {
        document.title = blink ? "DERS BASLADI" : " ";
        notificationSound
          .play()
          .catch((error) => console.error("Ses dosyası oynatılamadı:", error));
        blink = !blink;
      }, 1000);

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          clearInterval(interval);
          document.title = "Kaizu | Code2Work";
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPart, pin?.partMode]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-white/[0.06] flex items-center h-12">
        {isLive && pin?.partMode !== "ZOOM" && time && (
          <MyTimer
            expiryTimestamp={time}
            onExpire={() => next({ preventEmit: true })}
            cx="border-r border-white/[0.06] h-full flex items-center px-4"
          />
        )}

        {currentMl && (
          <div className="flex items-center gap-1.5 text-white/40 text-sm px-4 lg:px-5 min-w-0 overflow-hidden pl-12 lg:pl-5">
            <span className="truncate hidden sm:block">{currentMl?.ml_title}</span>
            {currentMl.ml_id > -1 && (
              <>
                {currentPart && currentPart.id > -1 && (
                  <>
                    <span className="text-white/15 hidden sm:block">/</span>
                    <span className="truncate hidden md:block">{currentPart.part_title}</span>
                  </>
                )}
                <span className="text-white/15 hidden sm:block">/</span>
                <span className="text-white/60 font-medium">{partModeLabels[pin!.partMode]}</span>
              </>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2 px-4 shrink-0">
          <FontSizeControl />
          <span className="hidden sm:block text-sm text-white/40">{user?.shortName}</span>
        </div>
      </div>

      {/* Content */}
      {isUnitFinished && <UnitFinished />}

      {!isUnitFinished && pin!.partMode === "PRACTICE" && currentPart && (
        <PracticeLoader partId={currentPart.id} />
      )}

      {!isUnitFinished && pin!.partMode === "VIDEO" && (
        <VideoMain
          url={"https://vimeo.com/" + currentPart?.video_url}
          setTime={setTime}
          isLive={isLive}
          onEnd={() => next({ preventEmit: true })}
          key={currentPart?.id}
        />
      )}

      {!isUnitFinished &&
        pin!.partMode === "ZOOM" &&
        !isLive &&
        (unit?.record_url ? (
          <VideoMain
            url={unit?.record_url}
            setTime={setTime}
          />
        ) : (
          <div className="bg-surface-1 border border-white/[0.08] rounded-2xl max-w-md p-12 mx-auto w-full mt-12 text-center">
            <div className="text-2xl text-myGreen font-semibold">Ders kaydı oluşturuluyor...</div>
            <div className="text-sm text-white/40 mt-4 leading-relaxed">İşlem tamamlandığında ders kaydı "Giriş" ve "Değerlendirme" bölümlerinde görünecek.</div>
            <Link
              to="/"
              className="inline-block mt-8 px-5 py-2.5 bg-myBlue hover:bg-myBlue/80 rounded-xl text-white text-sm font-semibold transition-colors"
            >
              Ana sayfaya dön
            </Link>
          </div>
        ))}

      <div
        className={clsx(
          "flex-1 zoomWrapper",
          shouldZoomVisible
            ? "block"
            : "opacity-0 absolute h-0 w-0 overflow-hidden"
        )}
      >
        {children}
      </div>
    </div>
  );
}
